import React, { Fragment, useMemo, useState } from 'react';
import { graphql } from '@apollo/client/react/hoc';
import { gql, useMutation } from '@apollo/client';
import styled from 'styled-components';
import { compose, withProps } from 'recompose';
import { RouterlessModal } from '@/se/components/Modal';
import ObjectInput from '@/se/components/inputs/ObjectInput';
import TextAreaInput from '@/se/components/inputs/TextAreaInput';
import { withLabel } from '@/se/components/Label';
import Form from '@/se/components/Form';
import get from 'lodash/get';
import pickBy from 'lodash/pickBy';
import isEmpty from 'lodash/isEmpty';
import fromPairs from 'lodash/fromPairs';
import SelectInput from '@/se/components/inputs/SelectInput';
import { create, remove, update } from '../../../../graph/bookings';
import { addMinutes, addWeeks, addMonths, format, isBefore, setHours, setMinutes } from 'date-fns';
import {
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  FormHelperText,
  Checkbox,
  FormGroup,
  Typography,
} from '@material-ui/core';

import NumberInput from '@/se/components/inputs/NumberInput';
import pluralize from 'pluralize';
import Button from '@/se/components/Button[DEPRECATED]';
import { CustomModal } from '../../kiosk/tablet/OperationRoomTabletV1';
import { getMinutesOfDay } from '../../../../util/dateTime';
import ProceduresPreview from './ProceduresPreview';
import { fragments as physicianFragments } from '../../../../graph/physicians';
import { fragments as partnerFragments } from '../../../../graph/partners';
import Grid from '@material-ui/core/Grid';
import LinkButton from '@/se/components/LinkButton';

const notF = fn => value => !fn(value);

const Wrapper = styled.div`
  display: flex;
  // margin-bottom: 2em;

  > * {
    flex: 1;
  }

  > * + * {
    margin-left: 0.666666em;
  }
`;

const BlockDateAndTime = styled.div`
  font-size: 1.333em;
`;

const BlockInfo = styled.div`
  margin-bottom: 1.5em;
  min-height: 2.5em;
`;

const HalfColumn = styled(Wrapper)`
  margin-bottom: 0;
`;

const ForInput = compose(withLabel('For'), withProps({ required: true }))(SelectInput);

const constructTimeOptions = (from, to) => {
  const label = format(from, 'h:mm A');
  const option = { [getMinutesOfDay(from)]: label === '12:00 PM' ? 'Noon' : label };
  return isBefore(from, to) ? { ...option, ...constructTimeOptions(addMinutes(from, 15), to) } : option;
};

const createTimeOptions = (dayStartTime = '07:00', dayEndTime = '15:00') => {
  const [startHour, startMinute] = dayStartTime.split(':').map(Number);
  const [endHour, endMinute] = dayEndTime.split(':').map(Number);

  return constructTimeOptions(
    setMinutes(setHours(new Date(), startHour), startMinute),
    setMinutes(setHours(new Date(), endHour), endMinute)
  );
};

const createFromInput = timeOptions =>
  compose(
    withLabel('From'),
    withProps({
      placeholder: 'From',
      options: timeOptions,
      required: false,
    })
  )(SelectInput);

const createToInput = timeOptions =>
  compose(
    withLabel('To'),
    withProps({
      placeholder: 'To',
      options: timeOptions,
      required: false,
    })
  )(SelectInput);

const repeatOptions = {
  0: `Don't Repeat`,
  1: 'Every Day',
  7: 'Every Week',
  14: 'Every 2 Weeks',
  21: 'Every 3 Weeks',
  28: 'Every 4 Weeks',
};

const weekOfMonthOptions = {
  1: '1st Week',
  2: '2nd Week',
  3: '3rd Week',
  4: '4th Week',
  5: '5th Week',
};

const occurrenceOfMonthOptions = {
  1: '1st Occurrence',
  2: '2nd Occurrence',
  3: '3rd Occurrence',
  4: '4th Occurrence',
  5: '5th Occurrence',
};

const monthOptions = {
  1: '1 Month',
  2: '2 Months',
  3: '3 Months',
  4: '4 Months',
  5: '5 Months',
  6: '6 Months',
  12: '12 Months',
};

const RepeatTypeSelector = ({ value, onChange, disabled }) => {
  const repeatType = value?.repeatType || 'none';

  const handleRepeatTypeChange = event => {
    const newType = event.target.value;
    onChange({
      ...value,
      repeatType: newType,
      repeat: newType === 'weekly' ? value?.repeat || 7 : newType === 'everyOtherWeek' ? 14 : 0,
      weekOfMonth: newType === 'weekOfMonth' ? value?.weekOfMonth || 1 : null,
      occurrenceOfMonth: newType === 'occurrenceOfMonth' ? value?.occurrenceOfMonth || [1] : null,
    });
  };

  return (
    <FormControl component="fieldset" disabled={disabled} style={{ marginBottom: '1em' }}>
      <FormLabel component="legend" style={{ fontWeight: 500, marginBottom: '0.5em' }}>
        Repeat Type
      </FormLabel>
      <RadioGroup row value={repeatType} onChange={handleRepeatTypeChange} style={{ marginBottom: '0.5em' }}>
        <FormControlLabel value="none" control={<Radio color="primary" />} label="Don't Repeat" />
        <FormControlLabel value="weekly" control={<Radio color="primary" />} label="Every Week" />
        <FormControlLabel value="everyOtherWeek" control={<Radio color="primary" />} label="Every Other Week" />
        <FormControlLabel value="weekOfMonth" control={<Radio color="primary" />} label="Week of Month" />
        <FormControlLabel value="occurrenceOfMonth" control={<Radio color="primary" />} label="Occurrence of Month" />
      </RadioGroup>
      <FormHelperText style={{ fontSize: '0.875rem', color: '#666' }}>
        {repeatType === 'weekOfMonth' && 'Creates bookings monthly on the specified week of each month'}
        {repeatType === 'occurrenceOfMonth' && 'Creates bookings monthly on the specified occurrences of that weekday'}
        {repeatType === 'weekly' && 'Creates bookings weekly with traditional intervals'}
        {repeatType === 'everyOtherWeek' && 'Creates bookings every other week'}
        {repeatType === 'none' && 'No repeating pattern'}
      </FormHelperText>
    </FormControl>
  );
};

const WeekOfMonthInput = compose(
  withLabel('Week of Month'),
  withProps({
    placeholder: 'Select week of month',
    options: weekOfMonthOptions,
    required: true,
  })
)(SelectInput);

const OccurrenceOfMonthSelector = ({ value, onChange, disabled }) => {
  const selectedOccurrences = value?.occurrenceOfMonth || [1];

  const handleOccurrenceChange = occurrence => {
    const newOccurrences = selectedOccurrences.includes(occurrence)
      ? selectedOccurrences.filter(o => o !== occurrence)
      : [...selectedOccurrences, occurrence].sort();

    onChange({
      ...value,
      occurrenceOfMonth: newOccurrences,
    });
  };

  return (
    <FormControl component="fieldset" disabled={disabled} style={{ marginBottom: '1em' }}>
      <FormLabel component="legend" style={{ fontWeight: 500, marginBottom: '0.5em' }}>
        Occurrences of Month
      </FormLabel>
      <FormGroup row>
        {Object.entries(occurrenceOfMonthOptions).map(([key, label]) => (
          <FormControlLabel
            key={key}
            control={
              <Checkbox
                checked={selectedOccurrences.includes(parseInt(key))}
                onChange={() => handleOccurrenceChange(parseInt(key))}
                color="primary"
              />
            }
            label={label}
          />
        ))}
      </FormGroup>
      <FormHelperText style={{ fontSize: '0.875rem', color: '#666' }}>
        Select which occurrences of this weekday to book each month
      </FormHelperText>
    </FormControl>
  );
};

const MonthsInput = compose(
  withLabel('Number of Months'),
  withProps({
    placeholder: 'Select number of months',
    options: monthOptions,
    required: true,
  })
)(SelectInput);

const RepeatInput = compose(
  withLabel('Repeat'),
  withProps({
    placeholder: 'Is it a repeating booking ?',
    options: repeatOptions,
  })
)(SelectInput);

const EndsAfterInput = compose(
  withLabel('Ends after'),
  withProps({
    min: 1,
    unitForValue: value => pluralize('Week', value),
    allowEmpty: true,
  })
)(NumberInput);

const BookerInput = ({ value, data, loading, onChange }) => {
  const options = useMemo(() => {
    if (!data || data.loading || data.error) {
      return {};
    } else {
      return pickBy(
        {
          'Open Blocks': { open: 'Anyone' },
          'Surgeon Offices': fromPairs((data.partners || []).map(partner => [`partner-${partner.id}`, partner.name])),
          Physicians: fromPairs(
            (data.physicians || []).map(physician => [`physician-${physician.id}`, physician.name])
          ),
        },
        notF(isEmpty)
      );
    }
  }, [data]);

  return (
    <Wrapper>
      <ForInput onChange={onChange} value={value} options={options} loading={loading} />
    </Wrapper>
  );
};

const RepeatPreview = styled.div`
  margin-top: 0.4em;
  word-wrap: break-word;
  overflow-wrap: break-word;
  line-height: 1.4;
  b {
    font-weight: 500;
  }
`;

const BlockInput = ({ value, onChange, dayStartTime = '07:00', dayEndTime = '15:00' }) => {
  const onChangeFactory = field => val => {
    onChange({ ...value, [field]: val });
  };

  const repeatType = get(value, 'repeatType', 'none');
  const repeats = repeatType !== 'none';
  const repeatWeeks = get(value, 'endsAfterWeeks', 1);
  const disabled = get(value, 'disabled', false);

  const timeOptions = useMemo(() => createTimeOptions(dayStartTime, dayEndTime), [dayStartTime, dayEndTime]);
  const FromInput = useMemo(() => createFromInput(timeOptions), [timeOptions]);
  const ToInput = useMemo(() => createToInput(timeOptions), [timeOptions]);

  const getRepeatPreviewText = () => {
    if (!repeats) return null;

    const date = get(value, 'date');
    const dayName = format(date, 'dddd');

    switch (repeatType) {
      case 'weekly':
        const repeatValue = get(value, 'repeat', 0);
        return (
          <RepeatPreview>
            Repeats&nbsp;
            <b>{repeatOptions[repeatValue]}</b>
            &nbsp;for&nbsp;
            <b>
              {repeatWeeks}&nbsp;{pluralize('Week', repeatWeeks)}
            </b>
            &nbsp;( {format(addWeeks(date, repeatWeeks), 'ddd, MMM Do, YYYY')} )
          </RepeatPreview>
        );
      case 'everyOtherWeek':
        return (
          <RepeatPreview>
            Repeats&nbsp;
            <b>Every 2 Weeks</b>
            &nbsp;for&nbsp;
            <b>
              {repeatWeeks}&nbsp;{pluralize('Week', repeatWeeks)}
            </b>
            &nbsp;( {format(addWeeks(date, repeatWeeks), 'ddd, MMM Do, YYYY')} )
          </RepeatPreview>
        );
      case 'weekOfMonth':
        const weekOfMonth = get(value, 'weekOfMonth', 1);
        const monthsForWeek = get(value, 'endsAfterWeeks', 1);
        const endDateForWeek = addMonths(date, monthsForWeek);
        return (
          <RepeatPreview>
            Repeats&nbsp;
            <b>Every {weekOfMonthOptions[weekOfMonth]} of the month</b>
            &nbsp;for&nbsp;
            <b>
              {monthsForWeek}&nbsp;{pluralize('Month', monthsForWeek)}
            </b>
            &nbsp;( {format(endDateForWeek, 'ddd, MMM Do, YYYY')} )
          </RepeatPreview>
        );
      case 'occurrenceOfMonth':
        const occurrenceOfMonth = get(value, 'occurrenceOfMonth', [1]);
        const monthsForOccurrence = get(value, 'endsAfterWeeks', 1);
        const endDateForOccurrence = addMonths(date, monthsForOccurrence);
        return (
          <RepeatPreview>
            Repeats&nbsp;
            <b>
              Every {occurrenceOfMonth.map(o => occurrenceOfMonthOptions[o]).join(', ')} of {dayName} each month
            </b>
            &nbsp;for&nbsp;
            <b>
              {monthsForOccurrence}&nbsp;{pluralize('Month', monthsForOccurrence)}
            </b>
            &nbsp;( {format(endDateForOccurrence, 'ddd, MMM Do, YYYY')} )
          </RepeatPreview>
        );
      default:
        return null;
    }
  };

  return (
    <div>
      <BlockInfo>
        <BlockDateAndTime>{format(get(value, 'date'), 'ddd, MMM Do, YYYY')}</BlockDateAndTime>
        {getRepeatPreviewText()}
      </BlockInfo>
      <Wrapper>
        <HalfColumn>
          <FromInput
            onChange={onChangeFactory('from')}
            value={get(value, 'from')}
            menuWidthFollowContent
            // isDisabled={disabled}
          />
          <ToInput
            onChange={onChangeFactory('to')}
            value={get(value, 'to')}
            menuWidthFollowContent
            // isDisabled={disabled}
          />
        </HalfColumn>
      </Wrapper>

      <RepeatTypeSelector value={value} onChange={onChange} disabled={disabled} />

      {repeatType === 'weekly' && (
        <Wrapper style={{ marginBottom: '2em' }}>
          <RepeatInput
            onChange={onChangeFactory('repeat')}
            value={get(value, 'repeat')}
            menuWidthFollowContent
            isDisabled={disabled}
          />
          {repeats && (
            <EndsAfterInput
              onChange={onChangeFactory('endsAfterWeeks')}
              value={repeatWeeks}
              menuWidthFollowContent
              disabled={disabled}
            />
          )}
        </Wrapper>
      )}

      {repeatType === 'everyOtherWeek' && (
        <Wrapper style={{ marginBottom: '2em' }}>
          <EndsAfterInput
            onChange={onChangeFactory('endsAfterWeeks')}
            value={repeatWeeks}
            menuWidthFollowContent
            disabled={disabled}
          />
        </Wrapper>
      )}

      {repeatType === 'weekOfMonth' && (
        <Wrapper style={{ marginBottom: '2em' }}>
          <WeekOfMonthInput
            onChange={onChangeFactory('weekOfMonth')}
            value={get(value, 'weekOfMonth')}
            menuWidthFollowContent
            isDisabled={disabled}
          />
          <MonthsInput
            onChange={onChangeFactory('endsAfterWeeks')}
            value={get(value, 'endsAfterWeeks')}
            menuWidthFollowContent
            isDisabled={disabled}
          />
        </Wrapper>
      )}

      {repeatType === 'occurrenceOfMonth' && (
        <Wrapper style={{ marginBottom: '2em' }}>
          <OccurrenceOfMonthSelector value={value} onChange={onChange} disabled={disabled} />
          <MonthsInput
            onChange={onChangeFactory('endsAfterWeeks')}
            value={get(value, 'endsAfterWeeks')}
            menuWidthFollowContent
            isDisabled={disabled}
          />
        </Wrapper>
      )}
    </div>
  );
};

const BookingInput = withProps({
  schema: {
    booker: compose(
      graphql(gql`
        query bookers {
          physicians {
            ...PhysicianData
          }
          partners(partnershipRole: PROCEDURE_TRANSFERS) {
            ...PartnerData
          }
        }
        ${physicianFragments.all}
        ${partnerFragments.all}
      `)
    )(BookerInput),
    block: props => <BlockInput {...props} dayStartTime={props.dayStartTime} dayEndTime={props.dayEndTime} />,
    notes: compose(
      withLabel('Notes'),
      withProps({
        placeholder: 'Add notes here…',
        rows: 6,
        style: { resize: 'none' },
      })
    )(TextAreaInput),
  },
})(ObjectInput);

const Actions = styled.div`
  display: flex;
  justify-content: space-between;
`;

const MultipleBookingRemoval = CustomModal(
  'This is a repeat booking. Do you want to delete only this event or all occurrences after it as well? ',
  false,
  'This one only',
  'This and all events after it',
  'Cancel'
);

const OneBookingRemoval = CustomModal('The booking will be removed permanently. Are you sure?');

const Footer = ({ busy, onCancel, initialValue, setScheduledProcedures, closeModal, setSecondDialogOpen }) => {
  const {
    id,
    masterBookingId,
    block: { repeat, repeatType = 'none', weekOfMonth = null, occurrenceOfMonth = null },
  } = initialValue;
  const [prompting, setPrompting] = useState(false);

  const [removeBooking, { loading: loadingRemove }] = useMutation(remove);
  const [errorMessage, setErrorMessage] = useState(null);

  const onRemove = removeRest => async () => {
    try {
      await removeBooking({ variables: { id: id, removeRest: removeRest } });
      setPrompting(false);
      onCancel();
      closeModal && closeModal();
      setSecondDialogOpen(false);
    } catch (e) {
      const BookingHasScheduledProceduresErrorMessage =
        e?.graphQLErrors?.[0].message === 'BookingHasScheduledProceduresError'
          ? 'This block already has scheduled procedures.'
          : null;
      setErrorMessage(BookingHasScheduledProceduresErrorMessage);

      throw { message: BookingHasScheduledProceduresErrorMessage };
    }
  };
  const hasRepeatingBooking = masterBookingId || repeat > 0 || weekOfMonth || occurrenceOfMonth;
  const Prompt = hasRepeatingBooking ? MultipleBookingRemoval : OneBookingRemoval;

  return (
    <Actions>
      {prompting && (
        <Prompt
          working={false}
          handleConfirm={onRemove(false)}
          handleSecondaryConfirm={hasRepeatingBooking ? onRemove(true) : undefined}
          handleCancel={() => {
            setErrorMessage(null);
            setPrompting(false);
            setSecondDialogOpen(false);
          }}
          errorMessage={errorMessage}
        />
      )}
      <Button disabled={busy}>{id ? 'Update' : 'Create'}</Button>
      {id && (
        <LinkButton
          danger
          disabled={busy}
          onClick={() => {
            setPrompting(true);
            setSecondDialogOpen(true);
          }}
          type={'button'}
        >
          Delete
        </LinkButton>
      )}
    </Actions>
  );
};

const getId = name => value => {
  if (value && value.startsWith && value.startsWith(`${name}-`)) {
    return parseInt(value.substring(name.length + 1), 10);
  } else {
    return null;
  }
};

const getPhysicianId = getId('physician');
const getPartnerId = getId('partner');

const getRepeatType = booking => {
  const bookingRepeat = get(booking, 'repeat');
  const bookingWeekOfMonth = get(booking, 'weekOfMonth');
  const bookingOccurrenceOfMonth = get(booking, 'occurrenceOfMonth');

  if (bookingWeekOfMonth) return 'weekOfMonth';
  if (bookingOccurrenceOfMonth) return 'occurrenceOfMonth';

  if (bookingRepeat === 14) return 'everyOtherWeek';
  if (bookingRepeat > 0) return 'weekly';

  return 'none';
};

const ContextForm = ({
  date,
  booker,
  room,
  from,
  to,
  booking,
  onClose,
  dayStartTime = '07:00',
  dayEndTime = '15:00',
}) => {
  const isEdit = !!get(booking, 'id');

  const [secondDialogOpen, setSecondDialogOpen] = useState(false);

  const [createBooking, { loading: loadingCreate }] = useMutation(create);
  const [updateBooking, { loading: loadingUpdate }] = useMutation(update);

  const onSubmit = async value => {
    const repeatType = get(value, 'block.repeatType', 'none');
    const repeat =
      repeatType === 'weekly' ? parseInt(get(value, 'block.repeat', 0), 10) : repeatType === 'everyOtherWeek' ? 14 : 0;
    const weekOfMonth = repeatType === 'weekOfMonth' ? parseInt(get(value, 'block.weekOfMonth', 1), 10) : null;
    const occurrenceOfMonth = repeatType === 'occurrenceOfMonth' ? get(value, 'block.occurrenceOfMonth', [1]) : null;
    const physician = getPhysicianId(get(value, 'booker'));
    const organization = getPartnerId(get(value, 'booker'));

    const fromTime = get(value, 'block.from');
    const toTime = get(value, 'block.to');

    const [startHour, startMinute] = dayStartTime.split(':').map(Number);
    const [endHour, endMinute] = dayEndTime.split(':').map(Number);
    const defaultFromMinutes = startHour * 60 + startMinute;
    const defaultToMinutes = endHour * 60 + endMinute;

    const fromMinute = fromTime ? parseInt(fromTime, 10) : defaultFromMinutes;
    const toMinute = toTime ? parseInt(toTime, 10) : defaultToMinutes;

    if (
      repeatType !== 'none' &&
      repeatType !== 'weekly' &&
      repeatType !== 'everyOtherWeek' &&
      repeatType !== 'weekOfMonth' &&
      repeatType !== 'occurrenceOfMonth'
    ) {
      throw { message: 'Please select a valid repeat type.' };
    }

    if (weekOfMonth && (weekOfMonth < 1 || weekOfMonth > 5)) {
      throw { message: 'Week of month must be between 1 and 5.' };
    }

    if (occurrenceOfMonth) {
      if (!Array.isArray(occurrenceOfMonth) || occurrenceOfMonth.length === 0) {
        throw { message: 'Please select at least one occurrence of the month.' };
      }
      for (const occurrence of occurrenceOfMonth) {
        if (occurrence < 1 || occurrence > 5) {
          throw { message: 'Occurrence of month values must be between 1 and 5.' };
        }
      }
    }

    if (weekOfMonth && occurrenceOfMonth) {
      throw { message: 'Cannot specify both Week of Month and Occurrence of Month simultaneously.' };
    }

    try {
      if (isEdit) {
        await updateBooking({
          variables: {
            id: get(booking, 'id'),
            masterBookingId: get(booking, 'masterBookingId'),
            physician,
            organization,
            roomId: get(room, 'id'),
            fromMinute: fromMinute,
            toMinute: toMinute,
            repeat: repeat,
            endsAfterWeeks: parseInt(
              get(value, 'block.endsAfterWeeks', repeat > 0 || weekOfMonth || occurrenceOfMonth ? 1 : 0),
              10
            ),
            date: format(date, 'YYYY-MM-DD'),
            notes: get(value, 'notes'),
            speciality: get(value, 'booker.physician.speciality.value') || get(value, 'physician.speciality'),
            updateRest: false,
            weekOfMonth: weekOfMonth,
            occurrenceOfMonth: occurrenceOfMonth,
          },
        });
        onClose(null);
      } else {
        await createBooking({
          variables: {
            id: get(booking, 'id'),
            masterBookingId: get(booking, 'masterBookingId'),
            physician,
            organization,
            roomId: get(room, 'id'),
            fromMinute: fromMinute,
            toMinute: toMinute,
            repeat: repeat,
            endsAfterWeeks: parseInt(
              get(value, 'block.endsAfterWeeks', repeat > 0 || weekOfMonth || occurrenceOfMonth ? 1 : 0),
              10
            ),
            date: format(date, 'YYYY-MM-DD'),
            notes: get(value, 'notes'),
            speciality: get(value, 'booker.physician.speciality.value') || get(value, 'physician.speciality'),
            weekOfMonth: weekOfMonth,
            occurrenceOfMonth: occurrenceOfMonth,
          },
        });
        onClose(null);
      }
    } catch (e) {
      const BookingHasScheduledProceduresErrorMessage =
        e?.graphQLErrors?.[0].message === 'BookingHasScheduledProceduresError'
          ? 'This block already has scheduled procedures.'
          : null;
      const OverlappingBookingErrorMessage =
        e?.graphQLErrors?.[0].message === 'OverlappingBookingError' ? 'This block overlaps with other blocks. ' : null;

      throw { message: BookingHasScheduledProceduresErrorMessage || OverlappingBookingErrorMessage };
    }
  };
  const closeModal = () => {
    onClose(null);
  };

  return (
    <RouterlessModal
      title={`Book ${get(room, 'name')}`}
      onClose={closeModal}
      width={booking ? '70rem' : '45rem'}
      style={{ overflowY: secondDialogOpen ? 'unset' : 'auto' }}
    >
      <Grid container spacing={2}>
        <Grid item md>
          <Form
            autoFocus
            input={props => <BookingInput {...props} dayStartTime={dayStartTime} dayEndTime={dayEndTime} />}
            onSubmit={onSubmit}
            busy={loadingCreate || loadingUpdate}
            initialValue={{
              id: get(booking, 'id'),
              masterBookingId: get(booking, 'masterBookingId'),
              block: {
                from: from,
                to: to,
                date,
                repeat: get(booking, 'repeat'),
                endsAfterWeeks: get(booking, 'endsAfterWeeks'),
                disabled: isEdit,
                repeatType: getRepeatType(booking),
                weekOfMonth: get(booking, 'weekOfMonth'),
                occurrenceOfMonth: get(booking, 'occurrenceOfMonth'),
              },
              booker,
              notes: get(booking, 'notes'),
            }}
            closeModal={closeModal}
            setSecondDialogOpen={setSecondDialogOpen}
            Footer={Footer}
          />
        </Grid>

        {booking && !secondDialogOpen && (
          <Grid item md>
            <ProceduresPreview date={date} room={room} showOverlay={false} />
          </Grid>
        )}
      </Grid>
    </RouterlessModal>
  );
};

export default ContextForm;
